import { useCallback } from 'react';

export default function useApi(API) {
  const token = localStorage.getItem('user_token');
  const headers = { 'Content-Type': 'application/json', ...(token ? { Authorization: `Bearer ${token}` } : {}) };

  const fetchItems = useCallback(async () => {
    const res = await fetch(`${API}/souk-clothes/items`, { headers });
    return res.json();
  }, [API, headers]);

  const fetchMerchants = useCallback(async () => {
    const res = await fetch(`${API}/souk-clothes/merchants`, { headers });
    return res.json();
  }, [API, headers]);

  const fetchOutfits = useCallback(async () => {
    const res = await fetch(`${API}/souk-clothes/outfits`, { headers });
    return res.json();
  }, [API, headers]);

  const fetchSettings = useCallback(async () => {
    const res = await fetch(`${API}/souk-clothes/settings`, { headers });
    return res.json();
  }, [API, headers]);

  const fetchAreas = useCallback(async () => {
    const res = await fetch(`${API}/souk-clothes/areas`, { headers });
    return res.json();
  }, [API, headers]);

  const fetchHelp = useCallback(async () => {
    const res = await fetch(`${API}/souk-clothes/help`);
    return res.json();
  }, [API]);

  const fetchGameState = useCallback(async (userId) => {
    const res = await fetch(`${API}/souk-clothes/state/${userId}`, { headers });
    return res.json();
  }, [API, headers]);

  const saveGameState = useCallback(async (userId, stateData) => {
    const res = await fetch(`${API}/souk-clothes/state/${userId}`, {
      method: 'PUT', headers, body: JSON.stringify(stateData)
    });
    return res.json();
  }, [API, headers]);

  const completeMission = useCallback(async (userId, data) => {
    const res = await fetch(`${API}/souk-clothes/complete-mission/${userId}`, {
      method: 'POST', headers, body: JSON.stringify(data)
    });
    return res.json();
  }, [API, headers]);

  const claimDailyReward = useCallback(async (userId) => {
    const res = await fetch(`${API}/souk-clothes/daily-reward/${userId}`, {
      method: 'POST', headers
    });
    return res.json();
  }, [API, headers]);

  const randomizeInventories = useCallback(async () => {
    const res = await fetch(`${API}/souk-clothes/randomize-inventories`, { method: 'POST', headers });
    return res.json();
  }, [API, headers]);

  const tradeItem = useCallback(async (userId, data) => {
    const res = await fetch(`${API}/souk-clothes/trade/${userId}`, {
      method: 'POST', headers, body: JSON.stringify(data)
    });
    return res.json();
  }, [API, headers]);

  const claimChallenge = useCallback(async (userId, data) => {
    const res = await fetch(`${API}/souk-clothes/claim-challenge/${userId}`, {
      method: 'POST', headers, body: JSON.stringify(data)
    });
    return res.json();
  }, [API, headers]);

  const sellItem = useCallback(async (userId, data) => {
    const res = await fetch(`${API}/souk-clothes/sell/${userId}`, {
      method: 'POST', headers, body: JSON.stringify(data)
    });
    return res.json();
  }, [API, headers]);

  const fetchTradeListings = useCallback(async () => {
    const res = await fetch(`${API}/souk-clothes/trade-listings`, { headers });
    return res.json();
  }, [API, headers]);

  const fetchMyListings = useCallback(async (userId) => {
    const res = await fetch(`${API}/souk-clothes/trade-listings/mine`, { headers });
    return res.json();
  }, [API, headers]);

  const createTradeListing = useCallback(async (data) => {
    const res = await fetch(`${API}/souk-clothes/trade-listings`, {
      method: 'POST', headers, body: JSON.stringify(data)
    });
    return res.json();
  }, [API, headers]);

  const acceptTradeListing = useCallback(async (listingId, data) => {
    const res = await fetch(`${API}/souk-clothes/trade-listings/${listingId}/accept`, {
      method: 'POST', headers, body: JSON.stringify(data)
    });
    return res.json();
  }, [API, headers]);

  const cancelTradeListing = useCallback(async (listingId) => {
    const res = await fetch(`${API}/souk-clothes/trade-listings/${listingId}`, {
      method: 'DELETE', headers
    });
    return res.json();
  }, [API, headers]);

  const fetchTradeNotifications = useCallback(async (userId) => {
    const res = await fetch(`${API}/souk-clothes/trade-notifications/${userId}`, { headers });
    return res.json();
  }, [API, headers]);

  const sendTradeOffer = useCallback(async (listingId, data) => {
    const res = await fetch(`${API}/souk-clothes/trade-listings/${listingId}/offer`, {
      method: 'POST', headers, body: JSON.stringify(data)
    });
    return res.json();
  }, [API, headers]);

  const respondToOffer = useCallback(async (offerId, data) => {
    const res = await fetch(`${API}/souk-clothes/trade-offers/${offerId}/respond`, {
      method: 'POST', headers, body: JSON.stringify(data)
    });
    return res.json();
  }, [API, headers]);

  const markNotificationsRead = useCallback(async (userId) => {
    const res = await fetch(`${API}/souk-clothes/trade-notifications/${userId}/read`, {
      method: 'POST', headers
    });
    return res.json();
  }, [API, headers]);

  const getUnreadCount = useCallback(async (userId) => {
    const res = await fetch(`${API}/souk-clothes/trade-notifications/${userId}/count`, { headers });
    return res.json();
  }, [API, headers]);

  const getMarketplaceName = useCallback(async (userId) => {
    const res = await fetch(`${API}/souk-clothes/marketplace-name/${userId}`, { headers });
    return res.json();
  }, [API, headers]);

  const regenerateMarketplaceName = useCallback(async (userId) => {
    const res = await fetch(`${API}/souk-clothes/marketplace-name/${userId}/regenerate`, {
      method: 'POST', headers
    });
    return res.json();
  }, [API, headers]);

  const buyListing = useCallback(async (listingId) => {
    const res = await fetch(`${API}/souk-clothes/trade-listings/${listingId}/buy`, {
      method: 'POST', headers
    });
    return res.json();
  }, [API, headers]);

  const fetchTradeHistory = useCallback(async (userId) => {
    const res = await fetch(`${API}/souk-clothes/trade-history/${userId}`, { headers });
    return res.json();
  }, [API, headers]);

  const fetchVocabWords = useCallback(async () => {
    const res = await fetch(`${API}/souk-clothes/vocab-words`, { headers });
    return res.json();
  }, [API, headers]);

  return {
    fetchItems, fetchMerchants, fetchOutfits, fetchSettings,
    fetchAreas, fetchHelp, fetchGameState, saveGameState,
    completeMission, claimDailyReward, randomizeInventories, tradeItem,
    claimChallenge, sellItem,
    fetchTradeListings, fetchMyListings, createTradeListing,
    acceptTradeListing, cancelTradeListing, fetchTradeNotifications,
    sendTradeOffer, respondToOffer, markNotificationsRead,
    getUnreadCount, getMarketplaceName, regenerateMarketplaceName,
    buyListing, fetchTradeHistory, fetchVocabWords
  };
}
